import { useState } from 'react';
import { aiBattleService } from '../services/aiBattleService';
import { usePoints } from './usePoints';
import type { User } from '../types/user';
import type { UserPet } from '../types/pets';
import type { UserItem } from '../types/items';
import type { BattleMove, BattleState, BattleEffect } from '../types/battle';

export function useAiBattle(user: User | null) {
  const [battleState, setBattleState] = useState<BattleState | null>(null);
  const [lastEffect, setLastEffect] = useState<BattleEffect | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updatePoints } = usePoints();

  const startBattle = async (pets: UserPet[], items: UserItem[], wagerAmount: number) => {
    if (!user) {
      setError('User must be logged in');
      return;
    }

    if (!pets.length) {
      setError('You must select at least one pet for battle');
      return;
    }
    
    try {
      setIsProcessing(true);
      setError(null);

      // Deduct wager amount
      await updatePoints(user, -wagerAmount);

      // Initialize battle state
      const initialState = aiBattleService.startBattle(user, pets, items, wagerAmount);
      setBattleState(initialState);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start battle';
      console.error('Battle start error:', errorMessage);
      setError(errorMessage);
      
      // Refund wager if battle fails to start
      await updatePoints(user, wagerAmount);
    } finally {
      setIsProcessing(false);
    }
  };

  const makeMove = async (move: BattleMove) => {
    if (!battleState || !user || isProcessing || battleState.gameOver) return;

    try {
      setIsProcessing(true);
      setError(null);

      // Process player move
      const playerState = aiBattleService.processPlayerMove(battleState, move);
      setBattleState(playerState.newState);
      setLastEffect(playerState.effects[0]);

      // Wait before AI response
      if (!playerState.newState.gameOver) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Process AI move
        const aiState = aiBattleService.processAiMove(playerState.newState);
        setBattleState(aiState.newState);
        setLastEffect(aiState.effects[0]);

        // Handle game over
        if (aiState.newState.gameOver) {
          const playerWon = aiState.newState.aiHealth <= 0;
          const reward = playerWon ? battleState.wagerAmount * 2 : 0;
          await updatePoints(user, reward);
        }
      } else {
        // Handle game over from player move
        const playerWon = playerState.newState.aiHealth <= 0;
        const reward = playerWon ? battleState.wagerAmount * 2 : 0;
        await updatePoints(user, reward);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process move';
      console.error('Move error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    battleState,
    lastEffect,
    isProcessing,
    error,
    startBattle,
    makeMove
  };
}